import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useIdleTimer } from "react-idle-timer";
import OwTitle from "../../shared/OwTitle/OwTitle";
import OwLabel from "../../shared/OwLabel/OwLabel";
import { useCrossTabIdleSync } from "../../../hooks/useCrossTabIdleSync";
import UiContext from "../../../contexts/UiContext";

type Props = {
    logoutFn: () => void;
};

// The time before a user is considered idle (30m)
const timeout = 1_800_000; // 30m x 60s x 1000ms
// How long before idle to emit the onPrompt event. - 30s before idle
const promptBeforeIdle = 30_000;

export const OwIdleTimer = (props: Props) => {
    const [remaining, setRemaining] = useState<number>(timeout);
    const [open, setOpen] = useState<boolean>(false);
    const uiContext = useContext(UiContext);
    const setIdleTimeRemainingRef = useRef(uiContext.setIdleTimeRemaining);

    // Update the ref when the function changes
    useEffect(() => {
        setIdleTimeRemainingRef.current = uiContext.setIdleTimeRemaining;
    }, [uiContext.setIdleTimeRemaining]);

    // Use a ref to store the broadcast function so we can access it in onAction
    const broadcastActivityRef = useRef<(() => void) | null>(null);

    /**
     * Function called when a user becomes idle from an active state.
     */
    const onIdle = useCallback(() => {
        // console.log('Idle')
        setOpen(false);
        // perform logout
        props.logoutFn();
    }, [props]);

    /**
     * Function called when a user becomes active from an idle state.
     */
    const onActive = useCallback(() => {
        // console.log('Active')
        setOpen(false);
    }, []);

    const onAction = useCallback(() => {
        // console.log('Action')
        setOpen(false);
        // Notify other tabs about activity
        if (broadcastActivityRef.current) {
            broadcastActivityRef.current();
        }
    }, []);

    const onPrompt = useCallback(() => {
        // console.log('Prompted')
        setOpen(true);
    }, []);

    /**
     * getRemainingTime Returns the time remaining in milliseconds until idle.
     * activate Resets the IdleTimer instance to its initial state, starts the timer and emits onActive if the user was idle.
     */
    const { getRemainingTime, activate } = useIdleTimer({
        onIdle,
        onActive,
        onAction,
        onPrompt,
        timeout,
        promptBeforeIdle,
        throttle: 500,
    });

    // Set up cross-tab synchronization
    const { broadcastActivity } = useCrossTabIdleSync({
        onActivityDetected: useCallback(() => {
            // When activity is detected in another tab, reset this tab's timer
            setOpen(false);
            activate();
        }, [activate]),
    });

    // Store the broadcast function in the ref so onAction can use it
    useEffect(() => {
        broadcastActivityRef.current = broadcastActivity;
    }, [broadcastActivity]);

    useEffect(() => {
        const interval = setInterval(() => {
            const _remainingTime = getRemainingTime() / 1000;

            // console.log(_remainingTime);

            const remainingSeconds = Math.ceil(_remainingTime);
            setRemaining(remainingSeconds);

            // Update the UiContext with the remaining time so other components can access it
            setIdleTimeRemainingRef.current(remainingSeconds);
        }, 500);

        return () => {
            clearInterval(interval);
        };
    }, [getRemainingTime]);

    return (
        <div
            className={"position-absolute"}
            style={{
                display: open ? "flex" : "none",
                color: "white",
                left: 0,
                top: 0,
                height: "100%",
                width: "100%",
                zIndex: 100,
                backgroundColor: "rgba(0,0,0,0.85)",
                alignItems: "center",
                justifyItems: "center",
                alignContent: "center",
                justifyContent: "center",
            }}>
            <div className="d-flex flex-column h-100 justify-content-center align-items-center">
                <OwTitle title={"session_timeout"} tag={"h2"} />
                <h1>{remaining}</h1>
                <OwTitle title={"seconds"} tag={"h5"} className={"text-small mb-5"} />
                <p className="text-small text-muted text-center">
                    <OwLabel title={"auto_logout_security_message_main"} leftIconClass={"far fa-lock"} noBottomMargin={true} />
                    <br />
                    <OwLabel title={"auto_logout_security_message_message"} noBottomMargin={true} />
                </p>
            </div>
        </div>
    );
};
